import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { id: "name", label: "Name", type: "text", required: true, testId: "contact-name-input" },
  { id: "organisation", label: "Organisation", type: "text", required: false, testId: "contact-organisation-input" },
  { id: "email", label: "Email", type: "email", required: true, testId: "contact-email-input" },
];

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", organisation: "", email: "", problem: "", additional: "", website: "" });
  const [state, setState] = useState("idle");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (state === "error") setState("idle");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setState("success");
    } catch {
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <div className="border border-glow/40 bg-glow/5 p-8" data-testid="contact-success-message">
        <div className="flex items-center gap-3">
          <Check size={18} className="shrink-0 text-glow" />
          <p className="text-lg font-medium text-slate-100">Received.</p>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          Expect a considered reply, not a sales sequence. If the problem is urgent, say so in a
          follow-up email and it will be treated that way.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
      {FIELDS.map((f) => (
        <div key={f.id}>
          <label htmlFor={`cf-${f.id}`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
            {f.label} {f.required && <span className="text-glow">*</span>}
          </label>
          <input
            id={`cf-${f.id}`}
            type={f.type}
            required={f.required}
            value={form[f.id]}
            onChange={set(f.id)}
            className="field-input"
            data-testid={f.testId}
          />
        </div>
      ))}
      <div>
        <label htmlFor="cf-problem" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
          What are you trying to solve? <span className="text-glow">*</span>
        </label>
        <textarea
          id="cf-problem"
          required
          rows={4}
          value={form.problem}
          onChange={set("problem")}
          placeholder="Describe what's happening — not what you think the solution is."
          className="field-input resize-y"
          data-testid="contact-problem-input"
        />
      </div>
      <div>
        <label htmlFor="cf-additional" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
          Anything else worth knowing
        </label>
        <textarea
          id="cf-additional"
          rows={3}
          value={form.additional}
          onChange={set("additional")}
          className="field-input resize-y"
          data-testid="contact-additional-input"
        />
      </div>
      <input
        type="text"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        name="website"
      />
      {state === "error" && (
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-red-400" data-testid="contact-error-message">
          Something didn't send — check the fields and try again.
        </p>
      )}
      <button type="submit" className="btn-primary" disabled={state === "loading"} data-testid="contact-submit-button">
        {state === "loading" ? "Sending…" : "Send the problem"}
        <ArrowRight size={14} />
      </button>
    </form>
  );
}
